type WsActorData = {
  userId: string;
  displayName: string;
  avatar: string | null;
};

export type WsOutboundData = {
  messageKey: string | null;
  params: Record<string, string> | null;
  parentId: string | null;
  targetId: string | null;
  gameId: string | null;
  hasNotification: boolean;
};

export type WsMessage = {
  type: string;
  data: WsOutboundData | null;
  actor: WsActorData | null;
};

/**
 * Parse a raw WS string into a WsMessage, or return null if invalid.
 * "pong" strings are treated as control frames and return null.
 */
export const parseWsMessage = (raw: string): WsMessage | null => {
  if (raw === 'pong') return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) return null;

  const message = parsed as Record<string, unknown>;

  if (!message.type || typeof message.type !== 'string') return null;

  // Backend sends actor as "user" field with "id"; normalize to "actor" with "userId"
  const rawActor = (message.user ?? message.actor) as
    | { id?: string; userId?: string; displayName?: string; avatar?: string | null }
    | undefined;
  const actor: WsActorData | null = rawActor
    ? {
        userId: rawActor.userId ?? rawActor.id ?? '',
        displayName: rawActor.displayName ?? '',
        avatar: rawActor.avatar ?? null,
      }
    : null;

  return {
    type: message.type as string,
    data: (message.data as WsOutboundData) ?? null,
    actor,
  };
};

/**
 * Build a subscribe message for WS topics.
 */
export const buildSubscribeMessage = (topics: string[]) => ({
  action: 'subscribe' as const,
  topics,
});

/**
 * Build an unsubscribe message for WS topics.
 */
export const buildUnsubscribeMessage = (topics: string[]) => ({
  action: 'unsubscribe' as const,
  topics,
});
